import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

// Type that works on v7 and v8
type VjsPlayer = ReturnType<typeof videojs>;

export type VideoJSPlayerProps = {
  src: string;
  type?: string;           // default video/mp4
  poster?: string;
  className?: string;
  onReady?: (ctx: { player: VjsPlayer; videoEl: HTMLVideoElement }) => void;
};

const VideoJSPlayer = forwardRef<HTMLVideoElement, VideoJSPlayerProps>(
  ({ src, type = 'video/mp4', poster, className, onReady }, ref) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const playerRef = useRef<VjsPlayer | null>(null);
    const mounted = useRef(false);

    useImperativeHandle(ref, () => videoRef.current as HTMLVideoElement, []);

    // init once (React 18 strict-mode safe)
    useEffect(() => {
      if (mounted.current) return;
      mounted.current = true;

      if (!videoRef.current) return;

      const player = (playerRef.current = videojs(videoRef.current, {
        controls: true,
        preload: 'auto',
        autoplay: false,
        sources: [{ src, type }],
        playbackRates: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]
      }));

      // underlying <video> element
      const techEl =
        ((player.tech(true) as any)?.el?.() as HTMLVideoElement) ||
        (player.el().getElementsByTagName('video')[0] as HTMLVideoElement);

      onReady?.({ player, videoEl: techEl });

      player.on('error', () => {
        console.error('[Video.js error]', player.error());
      });

      return () => {
        try {
          player.off('error');
          player.dispose();
        } finally {
          playerRef.current = null;
          mounted.current = false;
        }
      };
    }, []);

    // update source when prop changes
    useEffect(() => {
      const p = playerRef.current;
      if (p) p.src({ src, type });
    }, [src, type]);

    return (
      <div data-vjs-player>
        <video
          ref={videoRef}
          className={`video-js vjs-default-skin ${className ?? ''}`}
          poster={poster}
          playsInline
        />
      </div>
    );
  }
);

export default VideoJSPlayer;
